// src/features/admin/components/settings/integrations/components/IntegrationsDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ServerIcon,
  EnvelopeIcon,
  BuildingLibraryIcon,
  KeyIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { EdgeMailAdminAPI } from '@/features/edgemail-admin/services/EdgeMailAdminAPI';
import type { EmailStorageSettings, StorageProviderType } from '@/features/edgemail-admin/types/admin.types';

interface IntegrationStatus {
  category: string;
  name: string;
  status: 'connected' | 'error' | 'pending' | 'not_configured';
  lastSync?: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STORAGE_LABELS: Record<StorageProviderType, string> = {
  local: 'Local Server',
  gcs: 'Google Cloud Storage',
  s3: 'Amazon S3',
  azure: 'Azure Blob Storage',
  nas: 'On-Premise NAS',
};

const SUPPORTED_STORAGE_TYPES = new Set<StorageProviderType>(['local', 'gcs', 'nas']);

const DEFAULT_STORAGE_SETTINGS: EmailStorageSettings = {
  mode: 'unified',
  body: { type: 'local' },
  attachments: { type: 'local' },
};

const normalizeStorageSettings = (raw?: EmailStorageSettings): EmailStorageSettings => {
  if (!raw) return DEFAULT_STORAGE_SETTINGS;
  const mode = raw.mode === 'split' ? 'split' : 'unified';
  const body = { ...DEFAULT_STORAGE_SETTINGS.body, ...(raw.body || {}) };
  const attachments = mode === 'split'
    ? { ...DEFAULT_STORAGE_SETTINGS.attachments, ...(raw.attachments || {}) }
    : body;
  return { mode, body, attachments };
};

const buildEmailStorageStatuses = (storage?: EmailStorageSettings): IntegrationStatus[] => {
  if (!storage) {
    return [
      {
        category: 'Email Storage',
        name: 'Not configured',
        status: 'not_configured',
        lastSync: 'No tenant storage configuration found',
        icon: EnvelopeIcon,
      },
    ];
  }

  const normalized = normalizeStorageSettings(storage);
  const bodyType = normalized.body.type || 'local';
  const bodySupported = SUPPORTED_STORAGE_TYPES.has(bodyType);
  const bodyStatus: IntegrationStatus = {
    category: 'Email Storage',
    name: `Bodies: ${STORAGE_LABELS[bodyType] || bodyType}`,
    status: bodySupported ? 'connected' : 'pending',
    lastSync: bodySupported ? 'Active' : 'Provider not yet supported',
    icon: EnvelopeIcon,
  };

  if (normalized.mode !== 'split') {
    return [bodyStatus];
  }

  const attachmentType = normalized.attachments.type || bodyType;
  const attachmentSupported = SUPPORTED_STORAGE_TYPES.has(attachmentType);
  const attachmentStatus: IntegrationStatus = {
    category: 'Email Storage',
    name: `Attachments: ${STORAGE_LABELS[attachmentType] || attachmentType}`,
    status: attachmentSupported ? 'connected' : 'pending',
    lastSync: attachmentSupported ? 'Active' : 'Provider not yet supported',
    icon: EnvelopeIcon,
  };

  return [bodyStatus, attachmentStatus];
};

const STATIC_STATUSES: IntegrationStatus[] = [
  {
    category: 'Document Storage',
    name: 'Google Cloud Storage',
    status: 'connected',
    lastSync: 'Synced 2 minutes ago',
    icon: ServerIcon
  },
  {
    category: 'API Keys',
    name: 'SendGrid',
    status: 'connected',
    lastSync: 'Active',
    icon: KeyIcon
  },
  {
    category: 'API Keys',
    name: 'OpenAI',
    status: 'connected',
    lastSync: 'Active',
    icon: KeyIcon
  },
  {
    category: 'Court Systems',
    name: 'PACER',
    status: 'not_configured',
    icon: BuildingLibraryIcon
  },
  {
    category: 'Legal Research',
    name: 'LexisNexis',
    status: 'not_configured',
    icon: BuildingLibraryIcon
  }
];

const IntegrationsDashboard: React.FC = () => {
  const [integrationStatuses, setIntegrationStatuses] = useState<IntegrationStatus[]>([
    ...buildEmailStorageStatuses(undefined),
    ...STATIC_STATUSES,
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const config = await EdgeMailAdminAPI.getTenantConfig();
        const storage = (config.storage || (config.tenant?.settings?.email_storage as EmailStorageSettings | undefined));
        setIntegrationStatuses([
          ...buildEmailStorageStatuses(storage),
          ...STATIC_STATUSES,
        ]);
      } catch {
        setIntegrationStatuses([
          {
            category: 'Email Storage',
            name: 'Configuration error',
            status: 'error',
            lastSync: 'Failed to load tenant configuration',
            icon: EnvelopeIcon,
          },
          ...STATIC_STATUSES,
        ]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const summary = useMemo(() => ({
    connectedCount: integrationStatuses.filter(i => i.status === 'connected').length,
    pendingCount: integrationStatuses.filter(i => i.status === 'pending').length,
    errorCount: integrationStatuses.filter(i => i.status === 'error').length,
    notConfiguredCount: integrationStatuses.filter(i => i.status === 'not_configured').length,
  }), [integrationStatuses]);

  const emailStorageSummary = useMemo(() => {
    const storageEntries = integrationStatuses.filter(i => i.category === 'Email Storage');
    if (!storageEntries.length) return 'Email Storage: Not configured';

    const bodyEntry = storageEntries.find(e => e.name.toLowerCase().startsWith('bodies:'));
    const attachmentEntry = storageEntries.find(e => e.name.toLowerCase().startsWith('attachments:'));

    if (bodyEntry && attachmentEntry) {
      return `Email Storage: ${bodyEntry.name.replace('Bodies: ', '')} (Bodies), ${attachmentEntry.name.replace('Attachments: ', '')} (Attachments)`;
    }

    const activeName = storageEntries[0]?.name ?? 'Not configured';
    return `Email Storage: ${activeName.replace('Bodies: ', '')}`;
  }, [integrationStatuses]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Connected */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Connected</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{summary.connectedCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{emailStorageSummary}</p>
            </div>
          </div>
        </div>

        {/* Pending */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Setup</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{summary.pendingCount}</p>
            </div>
          </div>
        </div>

        {/* Errors */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Errors</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{summary.errorCount}</p>
            </div>
          </div>
        </div>

        {/* Available */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-lex-accent" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Available</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{summary.notConfiguredCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Status List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-lex-accent" />
            Integration Status Overview
          </h3>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {integrationStatuses.map((integration, index) => {
            const Icon = integration.icon;
            return (
              <div key={index} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mr-4">
                    <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {integration.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {integration.category}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {integration.lastSync && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {integration.lastSync}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(integration.status)}`}>
                    {getStatusIcon(integration.status)}
                    <span className="ml-1 capitalize">
                      {integration.status.replace('_', ' ')}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
              Refreshing integration status...
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-lex-accent-soft border border-lex-accent/30 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-lex-accent mb-2">
          Quick Actions
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button className="text-xs text-lex-accent hover:underline text-left">
            → Configure Email Storage
          </button>
          <button className="text-xs text-lex-accent hover:underline text-left">
            → Add API Key
          </button>
          <button className="text-xs text-lex-accent hover:underline text-left">
            → Test All Connections
          </button>
          <button className="text-xs text-lex-accent hover:underline text-left">
            → View Integration Logs
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsDashboard;
